import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Container, useMediaQuery, useTheme, CircularProgress, Typography } from "@mui/material";
import { Navigate, useSearchParams } from "react-router-dom";

import { ProfileSidebar } from "../components/ProfilePage/ProfileSidebar";
import { ProfileInfoSection } from "../components/ProfilePage/ProfileInfoSection";
import { ProfileAddressesSection } from "../components/ProfilePage/ProfileAddressesSection";
import { ProfileClientsSection } from "../components/ProfilePage/ProfileClientsSection";
import { ProfileOrdersSection } from "../components/ProfilePage/ProfileOrdersSection";
import { userStorage } from "../utils/storage";
import { HttpAuth } from "../http";

type Section = "info" | "direcciones" | "clientes" | "ordenes";

const parseTab = (v: string | null): Section | null => {
    if (v === "info" || v === "direcciones" || v === "clientes" || v === "ordenes") return v;
    return null;
};

export default function ProfilePage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = useMemo(() => parseTab(searchParams.get("tab")), [searchParams]);

    const [section, setSection] = useState<Section>(() => tabFromUrl ?? "info");
    const [isValidatingAuth, setIsValidatingAuth] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const validateAuth = async () => {
            try {
                await HttpAuth.getUser();
                setIsAuthenticated(true);
            } catch (error) {
                console.debug("Auth validation failed:", error);
                userStorage.removeFromStorage();
                setIsAuthenticated(false);
            } finally {
                setIsValidatingAuth(false);
            }
        };

        validateAuth();
    }, []);

    useEffect(() => {
        if (tabFromUrl) setSection(tabFromUrl);
    }, [tabFromUrl]);

    const handleSelect = useCallback(
        (s: Section) => {
            setSection(s);

            const next = new URLSearchParams(searchParams);
            next.set("tab", s);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );


    if (isValidatingAuth) {
        return (
            <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 6 }, pb: { xs: 6, md: 10 }, minHeight: "70vh" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "50vh", gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "14px", color: "text.secondary" }}>
                        Verificando autenticación...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <Container
            maxWidth="lg"
            sx={{
                pt: { xs: 3, md: 6 },
                pb: { xs: 6, md: 10 },
                minHeight: "70vh",
            }}
        >
            {/* WEB/DESKTOP:*/}
            {!isMobile ? (
                <Box sx={{ display: "flex", gap: 4 }}>
                    <ProfileSidebar section={section} onSelect={handleSelect} variant="sidebar" />

                    <Box sx={{ flex: 1 }}>
                        {section === "info" && <ProfileInfoSection />}
                        {section === "direcciones" && <ProfileAddressesSection />}
                        {section === "clientes" && <ProfileClientsSection />}
                        {section === "ordenes" && <ProfileOrdersSection />}
                    </Box>
                </Box>
            ) : (
                /* MOBILE: barra superior + contenido abajo */
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <ProfileSidebar section={section} onSelect={handleSelect} variant="topbar" />

                    <Box>
                        {section === "info" && <ProfileInfoSection />}
                        {section === "direcciones" && <ProfileAddressesSection />}
                        {section === "clientes" && <ProfileClientsSection />}
                        {section === "ordenes" && <ProfileOrdersSection />}
                    </Box>
                </Box>
            )}
        </Container>
    );
}














